// TeamProgress — admin view of who finished which lessons (ticket 212).
// Rendered only for seats that can manage training (staff.manage); the backend
// enforces the same gate. Loads on first expand so the page costs nothing for
// admins who never open it.
import React, { useState } from 'react';
import { Users, ChevronDown, ChevronRight, Award } from 'lucide-react';
import { api } from '../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';

const ROLE_LABELS = {
  admin: 'Admin',
  loan_officer: 'Loan officer',
  loan_officer_assistant: 'Loan officer assistant',
  processor: 'Processor',
  business_development: 'Business development',
};

function formatDay(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

function TrackCell({ track }) {
  const pct = track.lessons_count ? Math.round((track.lessons_completed / track.lessons_count) * 100) : 0;
  return (
    <div data-testid={`team-progress-${track.track_id}`}>
      <div className="flex items-center justify-between text-xs text-slate-600 mb-1 gap-2">
        <span>{track.lessons_completed}/{track.lessons_count} lessons</span>
        {track.certificate_issued_at && (
          <span className="inline-flex items-center gap-1 text-emerald-700" title={`Certificate issued ${formatDay(track.certificate_issued_at) || ''}`}>
            <Award size={12} /> Certified
          </span>
        )}
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  );
}

export default function TeamProgress() {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(false);

  const load = async () => {
    setError(false);
    try {
      const { data } = await api.get('/v2/training/team-progress');
      setMembers(data.members || []);
    } catch {
      setError(true);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && members === null) load();
  };

  const trackNames = members && members.length ? members[0].tracks : [];

  return (
    <Card className="mb-6 border border-slate-200" data-testid="team-progress-card">
      <button type="button" onClick={toggle} aria-expanded={open}
              data-testid="team-progress-toggle"
              className="w-full p-4 md:p-5 flex items-center gap-3 text-left">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-slate-100 text-[#070B5F]">
          <Users size={18} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm text-slate-900">Team progress</div>
          <div className="text-xs text-slate-600">See who has finished which lessons.</div>
        </div>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>

      {open && (
        <div className="px-4 md:px-5 pb-4 md:pb-5" data-testid="team-progress-body">
          {error && (
            <div className="text-sm text-red-700 flex items-center gap-3" data-testid="team-progress-error">
              Could not load team progress.
              <Button variant="outline" size="sm" onClick={load}>Try again</Button>
            </div>
          )}
          {!error && members === null && <div className="text-sm text-slate-500">Loading…</div>}
          {!error && members && members.length === 0 && (
            <div className="text-sm text-slate-500" data-testid="team-progress-empty">No team members yet.</div>
          )}
          {!error && members && members.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-progress-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-4 font-semibold">Member</th>
                    {trackNames.map((t) => (
                      <th key={t.track_id} className="py-2 pr-4 font-semibold min-w-[160px]">{t.name}</th>
                    ))}
                    <th className="py-2 font-semibold">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.user_id} className="border-t border-slate-100 align-top" data-testid={`team-progress-row-${m.user_id}`}>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-slate-900">{m.name}</div>
                        <div className="text-xs text-slate-500">{ROLE_LABELS[m.role] || m.role}</div>
                      </td>
                      {m.tracks.map((t) => (
                        <td key={t.track_id} className="py-3 pr-4"><TrackCell track={t} /></td>
                      ))}
                      <td className="py-3 text-slate-600">{formatDay(m.last_activity) || 'Not started'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
